import { differenceInDays } from 'date-fns';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconAddressCard } from '#src/atoms/icons/system/iconAddressCard.ts';
import { iconAlert2 } from '#src/atoms/icons/system/iconAlert2.ts';
import { iconClock } from '#src/atoms/icons/system/iconClock.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyS, LabelM } from '#src/atoms/typography/index.tsx';
import { useTickerContext } from '#src/contexts/ticker/index.tsx';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';

import { JonumKycPromptDialog } from '../JonumVerificationDialog/JonumKycPromptDialog.tsx';
import { useJonumVerificationGuard } from '../JonumVerificationDialog/useJonumVerificationGuard.ts';

const Card = styled(Vertical).attrs({ gap: 1.5 })<{ $pending?: boolean }>`
  width: 100%;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  ${({ $pending }) =>
    $pending
      ? css`
          background: rgba(var(--c-rgb-blue-400), 0.08);
          border: 1px solid rgba(var(--c-rgb-blue-400), 0.3);
        `
      : css`
          background: rgba(var(--c-rgb-yellow-400), 0.08);
          border: 1px solid rgba(var(--c-rgb-yellow-400), 0.3);
        `}
`;

const TitleIcon = styled.span<{ $pending?: boolean }>`
  color: ${({ $pending }) =>
    $pending ? 'var(--c-blue-400)' : 'var(--c-yellow-400)'};
`;

export const JonumKycReminder = () => {
  const {
    kycRequired,
    kycUnderReview,
    isProvisionallyVerified,
    gracePeriodExpiresAt,
  } = useJonumVerificationGuard();
  const { now } = useTickerContext();
  const [promptOpen, setPromptOpen] = useState(false);
  const {
    flags: { jonumCopyV2 = false },
  } = useFeatureFlags();

  if (!isProvisionallyVerified || !kycRequired) return null;

  if (kycUnderReview) {
    return (
      <Card $pending>
        <Horizontal gap={1}>
          <TitleIcon $pending>
            <FontAwesomeIcon icon={iconClock} />
          </TitleIcon>
          <LabelM bold color="var(--c-white)">
            <FormattedMessage defaultMessage="Verification pending" />
          </LabelM>
        </Horizontal>
        <BodyS color="var(--c-nd-600)">
          <FormattedMessage defaultMessage="We received your documents and are reviewing them. You'll be notified once your identity is verified." />
        </BodyS>
      </Card>
    );
  }

  const rawDaysLeft = gracePeriodExpiresAt
    ? differenceInDays(gracePeriodExpiresAt, now)
    : null;
  const gracePeriodExpired = rawDaysLeft !== null && rawDaysLeft <= 0;

  return (
    <>
      <Card>
        <Horizontal gap={1}>
          <TitleIcon>
            <FontAwesomeIcon icon={iconAlert2} />
          </TitleIcon>
          <LabelM bold color="var(--c-white)">
            {jonumCopyV2 ? (
              <FormattedMessage defaultMessage="Account not verified" />
            ) : (
              <FormattedMessage defaultMessage="Identity not verified" />
            )}
          </LabelM>
        </Horizontal>
        <BodyS color="var(--c-nd-600)">
          {jonumCopyV2 && gracePeriodExpired ? (
            <FormattedMessage defaultMessage="Grace period exceeded. Verify your account to continue playing JONUM competitions." />
          ) : rawDaysLeft === null || rawDaysLeft <= 0 ? (
            <FormattedMessage defaultMessage="Some rewards will only be awarded after we verify your identity. You will no longer be able to play once the grace period ends." />
          ) : (
            <FormattedMessage
              defaultMessage="Some rewards will only be awarded after we verify your identity. After {daysLeft, plural, one {# day} other {# days}}, you will no longer be able to play."
              values={{ daysLeft: rawDaysLeft }}
            />
          )}
        </BodyS>
        <Button
          size="medium"
          color="secondary"
          onClick={() => setPromptOpen(true)}
          startIcon={<FontAwesomeIcon icon={iconAddressCard} />}
          fullWidth
        >
          {jonumCopyV2 ? (
            <FormattedMessage defaultMessage="Verify my account" />
          ) : (
            <FormattedMessage defaultMessage="Verify my identity" />
          )}
        </Button>
      </Card>
      {!!promptOpen && (
        <JonumKycPromptDialog open onDone={() => setPromptOpen(false)} />
      )}
    </>
  );
};
